/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_HALL_ID } from 'helpers/config'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import LeadsAction from 'store/leads/LeadsAction'
import { Lead } from 'types/entities/Lead'
import { LeadCategory } from 'types/enums/LeadCategory'
import { LeadStatus } from 'types/enums/LeadStatus'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import {
    formatLeadDateField,
    getDefaultOpportunityCreatedDate,
    isClosingDateEditableStatus,
} from 'utils/leads/opportunityDateUtils'
import { createAssignmentReminderForEntity } from 'utils/reminders/createAssignmentReminder'
import { sanitizedInputs } from 'utils/utility'

type LeadInlinePatch = Partial<Pick<Lead, 'status' | 'category' | 'seller_id' | 'tags'>>

const fallbackLeadName = (lead: Lead): string =>
    lead.name || lead.company_name || lead.full_name || lead.contact_person || `Lead #${lead.id}`

/** Inline pill update — keeps existing lead id (create-or-update endpoint). */
export const buildLeadCreatePayload = (lead: Lead, patch: LeadInlinePatch): Partial<Lead> => ({
    id: lead.id,
    hall_id: lead.hall_id,
    name: fallbackLeadName(lead),
    seller_id: patch.seller_id ?? lead.seller_id,
    ...patch,
})

export type NewLeadCreatePayloadInput = {
    name: string
    status: LeadStatus
    isOpportunity?: boolean
    category?: LeadCategory | string | null
    tags?: string[] | null
    company_id?: number | null
    company_name?: string | null
    contact_id?: number | null
    contact_person?: string | null
    full_name?: string | null
    first_name?: string | null
    last_name?: string | null
    seller_id?: number | null
    hall_id?: number | null
    email?: string | null
    phone?: string | null
    website?: string | null
    source?: string | null
    referral_id?: string | number | null
    persons?: number | null
    requested_date?: string | null
    requested_time?: string | null
    created_date?: string | null
    closed_at?: string | null
    /** Existing lead id when updating; null/omit for create. */
    id?: number | null
}

/**
 * Same API body shape as manual lead/opportunity form save
 * (`useLeadBasicForm` → POST /admin/leads/create).
 */
export const buildNewLeadCreatePayload = (input: NewLeadCreatePayloadInput): Partial<Lead> => {
    const isOpportunity = Boolean(input.isOpportunity)
    const contactPerson =
        (input.contact_person || '').toString().trim() ||
        [input.first_name, input.last_name]
            .map((value) => (value || '').toString().trim())
            .filter(Boolean)
            .join(' ') ||
        (input.full_name || '').toString().trim()

    const payload: Record<string, unknown> = {
        name: input.name?.toString().trim(),
        company_name: (input.company_name || '')?.toString().trim() || null,
        email: input.email?.toString().trim() || undefined,
        phone: input.phone?.toString().trim() || undefined,
        website: input.website?.toString().trim() || undefined,
        category:
            input.category ??
            (isOpportunity ? LeadCategory.Opportunity : LeadCategory.Warm),
        status: input.status,
        source: input.source?.toString().trim() || null,
        referral_id:
            input.referral_id != null && String(input.referral_id).trim() !== ''
                ? input.referral_id
                : null,
        requested_date: input.requested_date || undefined,
        requested_time: input.requested_time || undefined,
        persons: input.persons || 0,
        tags: input.tags && input.tags.length > 0 ? input.tags : undefined,
        hall_id: input.hall_id || DEFAULT_HALL_ID,
        company_id: input.company_id ?? null,
        contact_id: input.contact_id ?? null,
    }

    // Only include id when updating an existing lead (manual form save).
    // Mass create params must not carry id — source ids go in the envelope `ids` array.
    if (input.id != null && Number.isFinite(Number(input.id))) {
        payload.id = Number(input.id)
    }

    if (isOpportunity) {
        payload.created_date =
            input.created_date ?? formatLeadDateField(getDefaultOpportunityCreatedDate())
        payload.closed_at = isClosingDateEditableStatus(input.status)
            ? input.closed_at ?? null
            : null
    }

    if (contactPerson) {
        payload.contact_person = contactPerson
        payload.full_name = contactPerson
    }

    if (input.seller_id != null && Number.isFinite(Number(input.seller_id))) {
        payload.seller_id = Number(input.seller_id)
    }

    return sanitizedInputs(payload) as Partial<Lead>
}

export const useInlineLeadPillUpdate = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const updateLeadInline = useCallback(
        async (lead: Lead, patch: LeadInlinePatch) => {
            await dispatch(LeadsAction.fetchCreateOrUpdateLead(buildLeadCreatePayload(lead, patch)))

            if (patch.seller_id != null && Number(patch.seller_id) !== Number(lead.seller_id)) {
                await createAssignmentReminderForEntity(dispatch as unknown as AppDispatch, {
                    modelName: ReminderModelName.Lead,
                    modelId: Number(lead.id),
                    sellerId: Number(patch.seller_id),
                    previousSellerId: lead.seller_id,
                    hallId: lead.hall_id,
                    content: t('Assigned to you') as string,
                })
            }
        },
        [dispatch, t]
    )

    return { updateLeadInline }
}
